import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import type { Invoice } from '../mocks/invoices';

const dbStatusToMock: Record<string, Invoice['status']> = {
  draft: 'Draft', sent: 'Sent', paid: 'Paid', overdue: 'Overdue', cancelled: 'Cancelled',
};

function rowToInvoice(r: Record<string, unknown>): Invoice {
  const total = (r.total_amount as number) || 0;
  const paid = r.paid_at ? total : 0;
  return {
    id: r.id as string,
    number: (r.invoice_number as string) || '',
    bookingId: (r.booking_id as string) || '',
    customer: '',
    customerAvatar: 'CU',
    customerEmail: '',
    customerPhone: '',
    customerAddress: '',
    issuedDate: (r.issue_date as string) || '',
    dueDate: (r.due_date as string) || '',
    status: dbStatusToMock[(r.status as string) || 'draft'] || 'Draft',
    lineItems: [],
    subtotal: (r.subtotal as number) || 0,
    discountPct: 0,
    discountAmt: (r.discount_amount as number) || 0,
    taxPct: (r.tax_rate as number) || 0,
    taxAmt: (r.tax_amount as number) || 0,
    totalAmount: total,
    paidAmount: paid,
    balanceDue: total - paid,
    currency: (r.currency as string) || 'AED',
    payments: [],
    notes: (r.notes as string) || '',
    agent: 'You',
  };
}

export function useInvoices() {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase.from('invoices').select('*').order('created_at', { ascending: false });
    setInvoices((data || []).map((r) => rowToInvoice(r as Record<string, unknown>)));
    setLoading(false);
  }, []);

  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

  const createInvoice = async (invoice: Partial<Invoice>) => {
    const num = `INV-${Date.now().toString().slice(-6)}`;
    const { data, error } = await supabase.from('invoices').insert({
      invoice_number: num,
      status: 'draft',
      issue_date: invoice.issuedDate || new Date().toISOString().slice(0, 10),
      due_date: invoice.dueDate || null,
      subtotal: invoice.subtotal || 0,
      tax_rate: invoice.taxPct || 0,
      tax_amount: invoice.taxAmt || 0,
      discount_amount: invoice.discountAmt || 0,
      total_amount: invoice.totalAmount || 0,
      currency: invoice.currency || 'AED',
      notes: invoice.notes,
    }).select().maybeSingle();
    if (!error && data) setInvoices((prev) => [rowToInvoice(data as Record<string, unknown>), ...prev]);
    return { data, error };
  };

  const updateStatus = async (id: string, status: Invoice['status']) => {
    const dbStatus = status.toLowerCase();
    const { error } = await supabase.from('invoices').update({
      status: dbStatus,
      paid_at: status === 'Paid' ? new Date().toISOString() : null,
    }).eq('id', id);
    if (!error) setInvoices((prev) => prev.map((inv) => inv.id === id ? { ...inv, status } : inv));
  };

  return { invoices, loading, refetch: fetchInvoices, createInvoice, updateStatus };
}
